import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '@/lib/api';

/**
 * Angebote (geteilte Kalkulationen) vom Server.
 * Ersetzt den frueheren localStorage-Hook useOffers, dessen Angebote den
 * Browser nie verlassen haben.
 */

export const deriveOfferStatus = (o) => {
  if (o.vollmacht_signed_at) return 'vollmacht';
  if (o.accepted_info) return 'beauftragt';
  if (Number(o.views) > 0) return 'angesehen';
  return 'gesendet';
};

export const useSharedOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch('/api/shared-kalkulationen');
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Laden fehlgeschlagen');
      setOffers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { gesamt: 0, gesendet: 0, angesehen: 0, beauftragt: 0, vollmacht: 0, offen: 0 };
    offers.forEach((o) => {
      const st = deriveOfferStatus(o);
      s.gesamt++;
      s[st]++;
    });
    // "offen" = versendet und noch nicht beauftragt
    s.offen = s.gesendet + s.angesehen;
    return s;
  }, [offers]);

  // Aktivitaetsstrom aus den echten Angebotsstaenden
  const activity = useMemo(() => {
    const items = [];
    offers.forEach((o) => {
      const name = o.company_name || o.contact_name || '–';
      if (o.vollmacht_signed_at) {
        items.push({ id: `${o.slug}-v`, type: 'vollmacht', company_name: name, slug: o.slug, created_at: o.vollmacht_signed_at });
      }
      if (o.accepted_info?.bestaetigt_am) {
        items.push({ id: `${o.slug}-a`, type: 'beauftragt', company_name: name, slug: o.slug, created_at: o.accepted_info.bestaetigt_am });
      }
      if (o.created_at) {
        items.push({ id: `${o.slug}-g`, type: 'gesendet', company_name: name, slug: o.slug, created_at: o.created_at });
      }
    });
    return items
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 6);
  }, [offers]);

  return { offers, stats, activity, loading, error, reload: load };
};

export default useSharedOffers;
